import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Router } from '@angular/router';

import { AuthUser, Permission, SecurityScope } from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { ScorecardTabShallow } from '../sc-header.state';

@Component({
    selector: 'cmps-sc-performers',
    templateUrl: './sc-performers.component.html',
})
export class ScPerformersComponent implements OnInit, OnDestroy {
    @Input() isCollapsed: boolean;
    @Input() useLiveData: boolean;

    @Input() tabs: ScorecardTabShallow[];
    @Input() tab: ScorecardTabShallow;

    @Output() tabChange: EventEmitter<string /* tabId */>;
    @Output() tabClose: EventEmitter<string /* tabId */>;
    @Output() addUser: EventEmitter<boolean /* tabId */>;
    @Output() exportScorecard: EventEmitter<void>;
    @Output() toggleUseLiveData: EventEmitter<boolean>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _user: AuthUser;
    /** @private */ _canViewTabMenu: boolean;
    /** @private */ _canUseLiveData: boolean;

    constructor(
        private readonly _router: Router,
        private readonly _authService: AuthService,
    ) {
        this.tabChange = new EventEmitter();
        this.tabClose = new EventEmitter();
        this.addUser = new EventEmitter();
        this.exportScorecard = new EventEmitter();
        this.toggleUseLiveData = new EventEmitter();
    }

    ngOnInit(): void {
        this._user = this._authService.user;

        // load permissions and check if the user can see tab options
        this._canViewTabMenu = this._authService.user.hasPermission(
            Permission.ADDITIONAL_TABS,
            SecurityScope.SCORECARD,
        );
        this._canUseLiveData =
            this._authService.user.hasPermission(
                Permission.VIEW_UNPUBLISHED_DATA,
                SecurityScope.SCORECARD,
            ) ||
            this._authService.user.hasPermission(
                Permission.PUBLISH_DATA,
                SecurityScope.SCORECARD,
            ) ||
            this._authService.user.hasPermission(
                Permission.GENERATE_DATA,
                SecurityScope.SCORECARD,
            );
    }

    ngOnDestroy(): void {
        this.addUser.complete();
        this.tabChange.complete();
        this.tabClose.complete();
        this.exportScorecard.complete();
    }

    /** @private */ _showTabTooltip(): boolean {
        const tabsWrapper =
            document
                .querySelector('cmps-tabset-label-wrapper')
                ?.getBoundingClientRect()?.width ?? 120;
        const maxNumberOfTabs = Math.floor(tabsWrapper / 120) - 1;
        return this.tabs.length > maxNumberOfTabs;
    }

    /** @private */ _cantAddMoreTabs(): boolean {
        const tabsWrapper =
            document
                .querySelector('cmps-tabset-label-wrapper')
                ?.getBoundingClientRect()?.width ?? 60;
        const maxNumberOfTabs = Math.floor(tabsWrapper / 60) - 1;
        return this.tabs.length >= maxNumberOfTabs;
    }

    /** @private */ _onTabChange(tabRefId: string): void {
        this.tabChange.emit(tabRefId);
    }

    /** @private */ _onTabClose(
        tabId: string,
        $event: MouseEvent | KeyboardEvent,
    ): void {
        $event.preventDefault();
        this.tabClose.emit(tabId);
    }

    /** @private */ _onCloseAllTabs($event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();
        this.tabs.slice(1).forEach((tab) => {
            this.tabClose.emit(tab.tabId);
        });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate(['pulse']);
    }

    /** @private */ _onAddUser($event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();
        this.addUser.emit(true);
    }

    /** @private */ _onExportClick($event: MouseEvent | KeyboardEvent): void {
        $event.preventDefault();
        this.exportScorecard.emit();
    }

    /** @private */ _onToggleUseLiveData(useLiveData: boolean): void {
        this.toggleUseLiveData.emit(useLiveData);
    }
}
